import { getConfigValueAsync } from '@pressreader/config';
import { httpDelete, httpGet, httpPost, httpPut, ImageUploadResult, uploadImage } from '@pressreader/services';

import {
    IAccountProfileConfigDto,
    IAccountProfileDto,
    IAddressDto,
    ILocationDto,
    IMastheadsModel,
    INotificationSettingsDto,
    ISetPasswordDto,
    IValidationResultDto,
} from './types';

const service = 'v1/accounts/current';

function normalizeProfile(profile: IAccountProfileDto) {
    // deliberate side-effect, there is no need to create new object,
    // because argument isn't used anywhere else
    const lastPasswordChangeDate = profile.lastPasswordChangeDate ?? '';
    if (lastPasswordChangeDate) {
        profile.lastPasswordChangeDate = new Date(lastPasswordChangeDate);
    }

    return profile;
}

async function getProfile(): Promise<IAccountProfileDto> {
    return httpGet<IAccountProfileDto>(service, 'profile').then(normalizeProfile);
}

async function getProfileConfig(): Promise<IAccountProfileConfigDto> {
    return httpGet<IAccountProfileConfigDto>(service, 'profile/config');
}

async function updateProfile(accountProfile: IAccountProfileDto): Promise<void> {
    return httpPut(service, 'profile', JSON.stringify(accountProfile), {
        contentType: 'application/json',
        disableRetry: true,
    });
}

async function updateAddress(address: IAddressDto): Promise<void> {
    return httpPut(service, 'address', JSON.stringify(address), {
        contentType: 'application/json',
        disableRetry: true,
    });
}

async function updateLocation(location: ILocationDto): Promise<void> {
    return httpPut(service, 'location', JSON.stringify(location), {
        contentType: 'application/json',
        disableRetry: true,
    });
}

async function setEmail(email: string): Promise<void> {
    return httpPut(service, 'email', JSON.stringify({ email }), {
        contentType: 'application/json',
        disableRetry: true,
    });
}

async function setLogonName(logonName: string): Promise<void> {
    return httpPut(service, 'logonName', JSON.stringify({ logonName }), {
        contentType: 'application/json',
        disableRetry: true,
    });
}

async function setPassword(password: ISetPasswordDto): Promise<void> {
    return httpPut(service, 'password', JSON.stringify(password), {
        contentType: 'application/json',
        disableRetry: true,
    });
}

async function getNotificationSettings(): Promise<INotificationSettingsDto> {
    return httpGet<INotificationSettingsDto>(service, 'notificationsettings');
}

async function setNotificationSettings(notificationSettings: INotificationSettingsDto): Promise<void> {
    return httpPut(service, 'notificationsettings', JSON.stringify(notificationSettings), {
        contentType: 'application/json',
        disableRetry: true,
    });
}

async function validateNickname(nickname: string): Promise<IValidationResultDto> {
    return httpPost(service, 'profile/nickname/validate', JSON.stringify(nickname), {
        contentType: 'application/json',
        disableRetry: true,
    });
}

async function validateProfileName(profileName: string): Promise<IValidationResultDto> {
    return httpPost(service, 'profile/profilename/validate', JSON.stringify(profileName), {
        contentType: 'application/json',
        disableRetry: true,
    });
}

async function deleteAccount() {
    return httpDelete(service);
}

async function getChannelMastheads(): Promise<IMastheadsModel> {
    return httpGet<IMastheadsModel>(service, 'mastheads');
}

async function uploadChannelMasthead(): Promise<ImageUploadResult> {
    const serverUrl = await getConfigValueAsync<string>('imageServer.upload.url');
    if (!serverUrl) {
        throw new Error('imageServer.upload.url is not configured');
    }

    return uploadImage(serverUrl, 'channel-mastheads');
}

async function updateChannelMastheads(model: IMastheadsModel): Promise<IMastheadsModel> {
    await httpPost(service, 'mastheads', JSON.stringify(model), { contentType: 'application/json; charset=utf-8' });
    return model;
}

async function deleteChannelMastheads(): Promise<void> {
    return httpDelete(service, 'mastheads', null, { accept: 'text/plain' });
}

export {
    deleteAccount,
    deleteChannelMastheads,
    getChannelMastheads,
    getNotificationSettings,
    getProfile,
    getProfileConfig,
    setEmail,
    setLogonName,
    setNotificationSettings,
    setPassword,
    updateAddress,
    updateChannelMastheads,
    updateLocation,
    updateProfile,
    uploadChannelMasthead,
    validateNickname,
    validateProfileName,
};
